import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import AlertToast from '../../components/common/AlertToast';
import PageHeader from '../../components/common/PageHeader';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', color: 'danger', desc: 'Full system access' },
  admin: { label: 'Administrator', color: 'warning', desc: 'System administration' },
  manager: { label: 'Manager', color: 'info', desc: 'Operations management' },
  team_lead: { label: 'Team Lead', color: 'primary', desc: 'Team coordination' },
  faculty: { label: 'Faculty', color: 'success', desc: 'Course instruction' },
  student: { label: 'Student', color: 'secondary', desc: 'Course enrollment' },
};

const formatCourseCategoryLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Student';
  return raw
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const isOnDemandCategory = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized.includes('on demand') || normalized.includes('ondemand') || normalized.includes('recorded');
};

const normalizeMediaUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const token = localStorage.getItem('token');
  const appendToken = (url) => {
    if (!token || /[?&]t=/.test(url)) return url;
    return url.includes('?') ? `${url}&t=${encodeURIComponent(token)}` : `${url}?t=${encodeURIComponent(token)}`;
  };
  if (/^(data:|blob:)/i.test(raw)) return raw;
  if (/^https?:/i.test(raw)) {
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const backendOrigin = apiBase
        ? apiBase.replace(/\/api\/?$/, '')
        : (typeof window !== 'undefined' ? window.location.origin : '');
      const parsed = new URL(raw);
      if (backendOrigin) return appendToken(`${backendOrigin}${parsed.pathname}${parsed.search || ''}`);
    } catch (_) {}
    return appendToken(raw);
  }
  return appendToken(raw);
};

const initialsOf = (...values) => values
  .join(' ')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join('') || 'TS';

const fmtDate = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtMoney = (value) => `Rs ${Number(value || 0).toLocaleString('en-IN')}`;
const iconChip = (bg, color) => ({
  width: 42,
  height: 42,
  borderRadius: 14,
  background: bg,
  color,
});
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const StudentProfileLayout = ({ user, tenant }) => {
  const navigate = useNavigate();
  const [settingsLogo, setSettingsLogo] = useState('');
  const [studentData, setStudentData] = useState({
    loading: true,
    plans: [],
    subscriptions: [],
    orders: [],
    learning: [],
    coupons: [],
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [plansRes, subscriptionsRes, ordersRes, learningRes, couponsRes] = await Promise.allSettled([
        api.get('/training/memberships/plans'),
        api.get('/training/memberships/my-subscriptions'),
        api.get('/training/memberships/my-orders'),
        api.get('/training/memberships/my-learning'),
        api.get('/training/memberships/coupons'),
      ]);

      if (!active) return;
      const readRows = (result) => {
        if (result.status !== 'fulfilled') return [];
        return toArray(result.value?.data?.data ?? result.value?.data);
      };

      setStudentData({
        loading: false,
        plans: readRows(plansRes),
        subscriptions: readRows(subscriptionsRes),
        orders: readRows(ordersRes),
        learning: readRows(learningRes),
        coupons: readRows(couponsRes),
      });
    };

    load().catch(() => {
      if (!active) return;
      setStudentData((prev) => ({ ...prev, loading: false }));
    });

    return () => { active = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    api.get('/settings')
      .then((res) => {
        if (!alive) return;
        const data = res?.data?.data || res?.data || {};
        const nextLogo = data?.branding?.logo
          || data?.branding?.logoUrl
          || data?.logo
          || data?.logoUrl
          || '';
        setSettingsLogo(nextLogo);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const cachedBranding = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('tenant_branding') || '{}');
    } catch (_) {
      return {};
    }
  }, []);

  const profileName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Student';
  const profileInitials = initialsOf(user?.firstName, user?.lastName, tenant?.name);
  const brandLogo = normalizeMediaUrl(
    tenant?.branding?.logo
    || tenant?.branding?.logoUrl
    || tenant?.logo
    || tenant?.logoUrl
    || settingsLogo
    || cachedBranding?.logo
    || cachedBranding?.logoUrl
    || ''
  );

  const activeSubscription = useMemo(
    () => studentData.subscriptions.find((row) => ['active', 'trial'].includes(String(row.status || '').toLowerCase())) || null,
    [studentData.subscriptions]
  );

  const freeMembershipPlan = useMemo(
    () => studentData.plans.find((plan) => (
      plan?.isActive !== false
      && Number(plan?.price || 0) <= 0
      && Number(plan?.salePrice || 0) <= 0
    )) || null,
    [studentData.plans]
  );

  const completedCourses = useMemo(
    () => studentData.learning.filter((row) => Number(row?.learningSummary?.completionPercent || 0) >= 100).length,
    [studentData.learning]
  );

  const totalHours = useMemo(
    () => Math.round(studentData.learning.reduce((sum, row) => sum + Number(row?.learningSummary?.timeSpentMinutes || 0), 0) / 60),
    [studentData.learning]
  );

  const startedCourses = useMemo(
    () => studentData.learning.filter((row) => Number(row?.learningSummary?.completionPercent || 0) > 0).length,
    [studentData.learning]
  );

  const primaryCategory = useMemo(() => {
    const fromLearning = studentData.learning.find((row) => row?.course?.category)?.course?.category;
    const fromSubscription = activeSubscription?.planSnapshot?.rules?.find((rule) => Array.isArray(rule?.categories) && rule.categories.length)?.categories?.[0];
    return fromLearning || fromSubscription || '';
  }, [activeSubscription, studentData.learning]);

  const isOfflineOnly = useMemo(() => {
    if (activeSubscription) return false;
    if (!studentData.learning.length) return false;
    return studentData.learning.every((row) => !isOnDemandCategory(row?.course?.category) && String(row?.course?.deliveryMode || '').toLowerCase() !== 'on_demand');
  }, [activeSubscription, studentData.learning]);

  const roleLabel = isOfflineOnly
    ? (formatCourseCategoryLabel(primaryCategory) || 'Offline Student')
    : (formatCourseCategoryLabel(primaryCategory) || 'On-demand Learner');

  const resolvedPlanName = activeSubscription?.planSnapshot?.name
    || activeSubscription?.planName
    || freeMembershipPlan?.name
    || (isOfflineOnly ? 'Course Access' : 'Free Membership');
  const currentPlanAmount = activeSubscription?.pricing?.finalAmount
    || activeSubscription?.planSnapshot?.salePrice
    || activeSubscription?.planSnapshot?.price
    || freeMembershipPlan?.salePrice
    || freeMembershipPlan?.price
    || 0;
  const currentPlanName = !isOfflineOnly && Number(currentPlanAmount || 0) <= 0
    ? (resolvedPlanName === 'Free Membership' ? resolvedPlanName : `${resolvedPlanName} · Free Membership`)
    : resolvedPlanName;
  const nextBillingDate = activeSubscription?.expiresAt || activeSubscription?.endDate || '';
  const featuredCoupon = studentData.coupons.find((row) => row?.status === 'approved' || row?.isActive);
  const activeLearnerCount = Math.max(startedCourses || 0, studentData.learning.length || 0, 1);
  const studentRating = `${Math.min(5, Math.max(4.2, 4.2 + (completedCourses * 0.2))).toFixed(1)}/5`;

  const overviewItems = [
    !isOfflineOnly ? {
      icon: 'bi-credit-card-2-front',
      iconBg: 'rgba(99,102,241,0.12)',
      iconColor: '#635bff',
      title: 'My Subscription',
      subtitle: 'Manage your plan, billing and renewal',
      value: currentPlanName,
      action: () => navigate('/lms?tab=my-subscription'),
    } : null,
    {
      icon: 'bi-bookmark',
      iconBg: 'rgba(34,197,94,0.12)',
      iconColor: '#16a34a',
      title: 'Saved Courses',
      subtitle: 'View and manage your saved courses',
      value: `${Number(user?.wishlist?.length || 0)}`,
      action: () => navigate('/lms?tab=my-courses'),
    },
    {
      icon: 'bi-award',
      iconBg: 'rgba(245,158,11,0.12)',
      iconColor: '#d97706',
      title: 'My Certificates',
      subtitle: 'View your earned certificates',
      value: `${completedCourses}`,
      action: () => navigate('/lms?tab=my-courses'),
    },
    !isOfflineOnly ? {
      icon: 'bi-wallet2',
      iconBg: 'rgba(59,130,246,0.12)',
      iconColor: '#2563eb',
      title: 'Payment Methods',
      subtitle: 'Manage cards, UPI and billing details',
      value: currentPlanAmount ? fmtMoney(currentPlanAmount) : 'Membership',
      action: () => navigate('/lms?tab=checkout'),
    } : null,
    {
      icon: 'bi-download',
      iconBg: 'rgba(168,85,247,0.12)',
      iconColor: '#9333ea',
      title: 'Downloads',
      subtitle: 'Access your downloaded lessons',
      value: `${studentData.learning.filter((row) => row?.course?.downloadable || row?.course?.resourceUrl).length}`,
      action: () => navigate('/lms?tab=my-courses'),
    },
  ].filter(Boolean);

  const supportItems = [
    {
      icon: 'bi-gift',
      iconBg: 'rgba(168,85,247,0.12)',
      iconColor: '#9333ea',
      title: 'Refer & Earn',
      subtitle: 'Invite friends and earn rewards',
      value: !isOfflineOnly && currentPlanAmount ? `Earn up to ${fmtMoney(currentPlanAmount)}` : '',
      action: () => navigate('/lms?tab=for-you'),
    },
    {
      icon: 'bi-question-circle',
      iconBg: 'rgba(59,130,246,0.12)',
      iconColor: '#2563eb',
      title: 'Help Center',
      subtitle: 'Get help and find answers',
      value: '',
      action: () => navigate('/notifications'),
    },
    {
      icon: 'bi-bell',
      iconBg: 'rgba(249,115,22,0.12)',
      iconColor: '#ea580c',
      title: 'Notifications',
      subtitle: 'Manage your notification preferences',
      value: '',
      action: () => navigate('/notifications'),
    },
    {
      icon: 'bi-gear',
      iconBg: 'rgba(148,163,184,0.16)',
      iconColor: '#64748b',
      title: 'Settings',
      subtitle: 'Manage account and app preferences',
      value: '',
      action: () => navigate('/profile'),
    },
    {
      icon: 'bi-trash3',
      iconBg: 'rgba(239,68,68,0.10)',
      iconColor: '#dc2626',
      title: 'Delete Account',
      subtitle: 'Permanently remove access to your account',
      value: '',
      action: () => navigate('/delete-account'),
    },
  ];

  return (
    <div className="d-flex flex-column gap-3">
      <div className="border bg-white" style={{ borderRadius: 24, padding: 20, borderColor: '#edf2ff', boxShadow: '0 18px 40px rgba(15,23,42,0.06)' }}>
        <div className="row g-3 align-items-start">
          <div className="col-12 col-xl-8">
            <div className="d-flex align-items-start gap-3">
              <div className="d-inline-flex align-items-center justify-content-center text-white fw-semibold" style={{ width: 84, height: 84, borderRadius: '50%', background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', fontSize: 32, flexShrink: 0 }}>
                {profileInitials}
              </div>
              <div className="flex-fill">
                <div className="fw-semibold" style={{ fontSize: 32, color: '#111827', lineHeight: 1.05 }}>{profileName}</div>
                <div className="mt-1" style={{ color: '#6b7280', fontSize: 18 }}>{roleLabel}</div>
                <div className="d-flex flex-wrap gap-4 mt-3">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-book-half" style={{ color: '#4f46e5' }} />
                    <div>
                      <div className="fw-semibold" style={{ color: '#111827' }}>{activeLearnerCount.toLocaleString('en-IN')}+</div>
                      <div className="small" style={{ color: '#6b7280' }}>Active learners</div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-star-fill" style={{ color: '#4f46e5' }} />
                    <div>
                      <div className="fw-semibold" style={{ color: '#111827' }}>{studentRating}</div>
                      <div className="small" style={{ color: '#6b7280' }}>Student rating</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-4">
            <div className="d-flex flex-column align-items-xl-end gap-3 h-100">
              <span className="badge rounded-pill" style={{ background: '#ffffff', color: '#374151', border: '1px solid #ececff', fontSize: 14, padding: '10px 16px' }}>
                {currentPlanName}
              </span>
              {!isOfflineOnly && (
                <button type="button" className="btn text-white" style={{ borderRadius: 999, padding: '12px 22px', fontWeight: 600, background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)', boxShadow: '0 14px 28px rgba(79,70,229,0.18)' }} onClick={() => navigate('/lms?tab=checkout')}>
                  <span className="d-inline-flex align-items-center justify-content-center me-2" style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', color: '#facc15', verticalAlign: 'middle' }}>
                    <i className="bi bi-gem" style={{ fontSize: 13, lineHeight: 1 }} />
                  </span>
                  Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isOfflineOnly && (
        <div className="border bg-white" style={{ borderRadius: 24, padding: 20, borderColor: '#ddd6fe', boxShadow: '0 18px 40px rgba(15,23,42,0.06)' }}>
          <div className="fw-semibold" style={{ color: '#111827', fontSize: 28 }}>Level up with Premium</div>
          <div className="small mt-1 mb-3" style={{ color: '#6b7280' }}>Unlock unlimited learning and career support.</div>
          <div className="row g-3 align-items-stretch">
            <div className="col-12 col-lg-5">
              <div className="h-100" style={{ borderRadius: 20, padding: 18, background: '#f8fbff', border: '1px solid #e6eefb' }}>
                <div className="small fw-semibold mb-2" style={{ color: '#4f46e5' }}>Your Current Plan</div>
                <div className="fw-semibold mb-3" style={{ color: '#111827', fontSize: 22 }}>{currentPlanName}</div>
                <div className="d-flex flex-column gap-2" style={{ color: '#334155' }}>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#22c55e' }} />Access to free courses</div>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#22c55e' }} />Recorded lessons</div>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#22c55e' }} />Practice quizzes</div>
                  <div><i className="bi bi-x-circle-fill me-2" style={{ color: '#94a3b8' }} />No live classes</div>
                  <div><i className="bi bi-x-circle-fill me-2" style={{ color: '#94a3b8' }} />No mentor support</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-2 d-flex align-items-center justify-content-center">
              <div className="d-inline-flex align-items-center justify-content-center fw-semibold" style={{ width: 56, height: 56, borderRadius: '50%', background: '#ffffff', border: '1px solid #e5e7eb', color: '#111827' }}>VS</div>
            </div>
            <div className="col-12 col-lg-5">
              <div className="h-100 position-relative" style={{ borderRadius: 20, padding: 18, background: 'linear-gradient(180deg, #fbf7ff 0%, #f3f0ff 100%)', border: '1px solid #ece9ff' }}>
                <span className="badge rounded-pill position-absolute" style={{ top: 16, right: 16, background: '#ffe7df', color: '#ea580c' }}>Most Popular</span>
                <div className="small fw-semibold mb-2" style={{ color: '#7c3aed' }}>Unlock with Pro Plan</div>
                <div className="d-flex flex-column gap-2 mt-4" style={{ color: '#312e81' }}>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#4f46e5' }} />Everything in Free Plan</div>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#4f46e5' }} />Live interactive classes</div>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#4f46e5' }} />1:1 mentor support</div>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#4f46e5' }} />Projects and case studies</div>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#4f46e5' }} />Verified certificate</div>
                  <div><i className="bi bi-check-circle-fill me-2" style={{ color: '#4f46e5' }} />Career guidance and placement help</div>
                </div>
                <div className="small mt-3" style={{ color: '#6d28d9' }}>
                  <i className="bi bi-gift me-2" />
                  {featuredCoupon?.code ? `${featuredCoupon.code} available for this plan` : 'Premium plans unlock more guided learning'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row g-3">
        <div className="col-12 col-xl-8">
          <div className="border overflow-hidden bg-white" style={{ borderRadius: 24, borderColor: '#edf2ff', boxShadow: '0 18px 40px rgba(15,23,42,0.06)' }}>
            {overviewItems.map((item, index) => (
              <button key={item.title} type="button" onClick={item.action} className="w-100 d-flex align-items-center justify-content-between gap-3 border-0 bg-transparent text-start" style={{ padding: '18px 20px', borderBottom: index === overviewItems.length - 1 ? 'none' : '1px solid #eef2f7' }}>
                <div className="d-flex align-items-start gap-3">
                  <div className="d-inline-flex align-items-center justify-content-center" style={iconChip(item.iconBg, item.iconColor)}>
                    <i className={`bi ${item.icon}`} />
                  </div>
                  <div>
                    <div className="fw-semibold" style={{ color: '#111827' }}>{item.title}</div>
                    <div className="small" style={{ color: '#6b7280' }}>{item.subtitle}</div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div className="small fw-semibold text-end" style={{ color: '#4f46e5' }}>{item.value}</div>
                  <i className="bi bi-chevron-right" style={{ color: '#94a3b8' }} />
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="col-12 col-xl-4">
          <div className="d-flex flex-column gap-3">
            <div className="border bg-white" style={{ borderRadius: 24, padding: 18, borderColor: '#edf2ff', boxShadow: '0 18px 40px rgba(15,23,42,0.06)' }}>
              <div className="fw-semibold mb-3" style={{ color: '#111827', fontSize: 20 }}>Profile Summary</div>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between gap-3"><span className="small" style={{ color: '#6b7280' }}>Completed courses</span><span className="small fw-semibold" style={{ color: '#111827' }}>{completedCourses}</span></div>
                <div className="d-flex justify-content-between gap-3"><span className="small" style={{ color: '#6b7280' }}>Learning hours</span><span className="small fw-semibold" style={{ color: '#111827' }}>{totalHours} hrs</span></div>
                <div className="d-flex justify-content-between gap-3"><span className="small" style={{ color: '#6b7280' }}>Current plan</span><span className="small fw-semibold" style={{ color: '#111827' }}>{currentPlanName}</span></div>
                {!isOfflineOnly && <div className="d-flex justify-content-between gap-3"><span className="small" style={{ color: '#6b7280' }}>Valid until</span><span className="small fw-semibold" style={{ color: '#111827' }}>{fmtDate(nextBillingDate)}</span></div>}
              </div>
            </div>

            {!isOfflineOnly && (
              <button type="button" className="btn w-100 text-white" style={{ borderRadius: 18, padding: '18px 20px', background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)', boxShadow: '0 18px 34px rgba(79,70,229,0.18)' }} onClick={() => navigate('/lms?tab=checkout')}>
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3 text-start">
                    <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.16)', color: '#facc15', flexShrink: 0 }}>
                      <i className="bi bi-gem fs-5" />
                    </div>
                    <div>
                      <div className="fw-semibold">Upgrade to Pro</div>
                      <div className="small" style={{ opacity: 0.88 }}>Unlock live classes, mentorship and more</div>
                    </div>
                  </div>
                  <i className="bi bi-chevron-right" />
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="border overflow-hidden bg-white" style={{ borderRadius: 24, borderColor: '#edf2ff', boxShadow: '0 18px 40px rgba(15,23,42,0.06)' }}>
        {supportItems.map((item, index) => (
          <button key={item.title} type="button" onClick={item.action} className="w-100 d-flex align-items-center justify-content-between gap-3 border-0 bg-transparent text-start" style={{ padding: '18px 20px', borderBottom: index === supportItems.length - 1 ? 'none' : '1px solid #eef2f7' }}>
            <div className="d-flex align-items-start gap-3">
              <div className="d-inline-flex align-items-center justify-content-center" style={iconChip(item.iconBg, item.iconColor)}>
                <i className={`bi ${item.icon}`} />
              </div>
              <div>
                <div className="fw-semibold" style={{ color: '#111827' }}>{item.title}</div>
                <div className="small" style={{ color: '#6b7280' }}>{item.subtitle}</div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              {item.value ? <div className="small fw-semibold" style={{ color: '#7c3aed' }}>{item.value}</div> : null}
              <i className="bi bi-chevron-right" style={{ color: '#94a3b8' }} />
            </div>
          </button>
        ))}
      </div>

      {!isOfflineOnly && (
        <div className="d-flex flex-column gap-3">
          <button
            type="button"
            className="btn text-white text-start"
            style={{ borderRadius: 20, padding: '18px 22px', background: 'linear-gradient(90deg, #2563eb 0%, #9333ea 100%)', boxShadow: '0 18px 34px rgba(79,70,229,0.18)' }}
            onClick={() => navigate('/lms?tab=checkout')}
          >
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', color: '#facc15' }}>
                  <i className="bi bi-gem fs-5" />
                </div>
                <div>
                  <div className="fw-semibold fs-5">Upgrade to Pro</div>
                  <div className="small" style={{ opacity: 0.92 }}>Unlock live classes, mentorship and more</div>
                </div>
              </div>
              <i className="bi bi-chevron-right fs-5" />
            </div>
          </button>
          <button
            type="button"
            className="btn bg-white text-start"
            style={{ borderRadius: 20, padding: '18px 22px', border: '1px solid #d9d8ff', boxShadow: '0 10px 24px rgba(15,23,42,0.05)' }}
            onClick={() => navigate('/notifications')}
          >
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(99,102,241,0.08)', color: '#6d28d9' }}>
                  <i className="bi bi-headset fs-4" />
                </div>
                <div>
                  <div className="fw-semibold" style={{ color: '#4c1d95', fontSize: 18 }}>Talk to Advisor</div>
                  <div className="small" style={{ color: '#6b7280' }}>Get personalized career guidance</div>
                </div>
              </div>
              <i className="bi bi-chevron-right" style={{ color: '#7c3aed' }} />
            </div>
          </button>
        </div>
      )}

      {studentData.loading && <div className="small text-muted">Loading profile details...</div>}
    </div>
  );
};

const StaffProfileLayout = () => {
  const { user, updateProfile } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    secondaryPhone: '',
    department: '',
    designation: '',
    address: { street: '', city: '', state: '', pincode: '' },
  });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        secondaryPhone: user.secondaryPhone || '',
        department: user.department || '',
        designation: user.designation || '',
        address: user.address || { street: '', city: '', state: '', pincode: '' },
      });
    }
  }, [user]);

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      if (updateProfile) {
        const result = await updateProfile(form);
        setToast({ type: result.success ? 'success' : 'danger', message: result.message });
        if (result.success) setEditMode(false);
      } else {
        await api.put('/auth/profile', form);
        setToast({ type: 'success', message: 'Profile updated!' });
        setEditMode(false);
      }
    } catch (e) {
      setToast({ type: 'danger', message: e.response?.data?.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        secondaryPhone: user.secondaryPhone || '',
        department: user.department || '',
        designation: user.designation || '',
        address: user.address || { street: '', city: '', state: '', pincode: '' },
      });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setToast({ type: 'danger', message: 'Passwords do not match.' });
      return;
    }
    if (pwForm.newPassword.length < 6) {
      setToast({ type: 'danger', message: 'New password must be at least 6 characters.' });
      return;
    }
    setPwSaving(true);
    try {
      await api.put('/auth/change-password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setToast({ type: 'success', message: 'Password changed successfully.' });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e) {
      setToast({ type: 'danger', message: e.response?.data?.message || 'Failed to change password.' });
    } finally {
      setPwSaving(false);
    }
  };

  const setAddr = (k, v) => setForm((p) => ({ ...p, address: { ...p.address, [k]: v } }));
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || '?';
  const roleConf = ROLE_CONFIG[user?.roleKey] || {};
  const joinedStr = user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : null;

  return (
    <div>
      {toast && <AlertToast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      <PageHeader title="My Profile" icon="bi-person-circle" subtitle="Manage your personal information and account settings" />
      <div className="row g-3">
        <div className="col-md-4 col-xl-3">
          <div className="card shadow-sm overflow-hidden mb-3">
            <div style={{ height: 90, background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #0d9488 100%)' }} />
            <div className="card-body pt-0 text-center">
              <div className="mx-auto border border-3 border-white rounded-circle d-flex align-items-center justify-content-center text-white fw-bold" style={{ width: 80, height: 80, marginTop: -40, background: 'linear-gradient(135deg, #2563eb, #0d9488)', fontSize: 28, boxShadow: '0 4px 15px rgba(37,99,235,0.35)' }}>{initials}</div>
              <h5 className="fw-bold mt-2 mb-0">{user?.firstName} {user?.lastName}</h5>
              <small className="text-muted">{user?.email}</small>
              <div className="mt-2"><span className={`badge bg-${roleConf.color || 'secondary'} me-1`}><i className="bi bi-shield-fill me-1" />{roleConf.label || user?.roleKey}</span></div>
              {roleConf.desc && <p className="text-muted mt-1 mb-0" style={{ fontSize: '0.75rem' }}>{roleConf.desc}</p>}
              <hr />
              <div className="text-start small">
                {user?.phone && <div className="d-flex align-items-center gap-2 mb-1"><i className="bi bi-phone text-muted" /><span>{user.phone}</span>{user.secondaryPhone && <span className="text-muted">/ {user.secondaryPhone}</span>}</div>}
                {user?.address?.city && <div className="d-flex align-items-center gap-2 mb-1"><i className="bi bi-geo-alt text-muted" /><span>{user.address.city}{user.address.state ? `, ${user.address.state}` : ''}</span></div>}
                {user?.department && <div className="d-flex align-items-center gap-2 mb-1"><i className="bi bi-building text-muted" /><span>{user.department}</span></div>}
                {joinedStr && <div className="d-flex align-items-center gap-2"><i className="bi bi-calendar3 text-muted" /><span>Joined {joinedStr}</span></div>}
              </div>
              <button className={`btn btn-sm w-100 mt-3 ${editMode ? 'btn-outline-secondary' : 'btn-primary'}`} onClick={editMode ? handleCancelEdit : () => setEditMode(true)}><i className={`bi ${editMode ? 'bi-x-lg' : 'bi-pencil'} me-1`} />{editMode ? 'Cancel Editing' : 'Edit Profile'}</button>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body py-2">
              <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>Account Status</small>
              <div className="d-flex justify-content-between align-items-center mt-2"><small>Status</small><span className={`badge bg-${user?.isActive !== false ? 'success' : 'danger'}`}>{user?.isActive !== false ? 'Active' : 'Inactive'}</span></div>
              <div className="d-flex justify-content-between align-items-center mt-1"><small>Role Level</small><small className="fw-semibold text-primary">{roleConf.label || user?.roleKey || '-'}</small></div>
              {user?.designation && <div className="d-flex justify-content-between align-items-center mt-1"><small>Designation</small><small className="fw-semibold">{user.designation}</small></div>}
            </div>
          </div>
        </div>

        <div className="col-md-8 col-xl-9">
          <div className="card shadow-sm mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span><i className="bi bi-person me-2" />Personal Information</span>
              {editMode && <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>{saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : <><i className="bi bi-check-lg me-1" />Save Changes</>}</button>}
            </div>
            <div className="card-body">
              <form onSubmit={handleSave}>
                <div className="row g-3">
                  <div className="col-md-6"><label className="form-label">First Name</label><input className="form-control" value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} disabled={!editMode} /></div>
                  <div className="col-md-6"><label className="form-label">Last Name</label><input className="form-control" value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} disabled={!editMode} /></div>
                  <div className="col-md-6"><label className="form-label">Email</label><input className="form-control bg-light" value={user?.email || ''} disabled title="Email cannot be changed" /></div>
                  <div className="col-md-6"><label className="form-label">Primary Phone</label><input className="form-control" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} disabled={!editMode} placeholder="+91 XXXXX XXXXX" /></div>
                  <div className="col-md-6"><label className="form-label">Secondary Phone</label><input className="form-control" value={form.secondaryPhone} onChange={(e) => setForm((p) => ({ ...p, secondaryPhone: e.target.value }))} disabled={!editMode} /></div>
                  <div className="col-md-6"><label className="form-label">Role</label><input className="form-control bg-light" value={user?.roleKey || ''} disabled /></div>
                  <div className="col-md-6"><label className="form-label">Department</label><input className="form-control" value={form.department} onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))} disabled={!editMode} /></div>
                  <div className="col-md-6"><label className="form-label">Designation</label><input className="form-control" value={form.designation} onChange={(e) => setForm((p) => ({ ...p, designation: e.target.value }))} disabled={!editMode} /></div>
                </div>
              </form>
            </div>
          </div>

          <div className="card shadow-sm mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span><i className="bi bi-geo-alt me-2" />Address</span>
              {editMode && <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}><i className="bi bi-check-lg me-1" />Save</button>}
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12"><label className="form-label">Street Address</label><input className="form-control" value={form.address?.street || ''} onChange={(e) => setAddr('street', e.target.value)} disabled={!editMode} /></div>
                <div className="col-md-4"><label className="form-label">City</label><input className="form-control" value={form.address?.city || ''} onChange={(e) => setAddr('city', e.target.value)} disabled={!editMode} /></div>
                <div className="col-md-4"><label className="form-label">State</label><input className="form-control" value={form.address?.state || ''} onChange={(e) => setAddr('state', e.target.value)} disabled={!editMode} /></div>
                <div className="col-md-4"><label className="form-label">Pincode</label><input className="form-control" value={form.address?.pincode || ''} onChange={(e) => setAddr('pincode', e.target.value)} disabled={!editMode} /></div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-header"><i className="bi bi-lock me-2" />Change Password</div>
            <div className="card-body">
              <form onSubmit={handleChangePassword}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Current Password</label>
                    <div className="input-group">
                      <input type={showPw ? 'text' : 'password'} className="form-control" value={pwForm.currentPassword} onChange={(e) => setPwForm((p) => ({ ...p, currentPassword: e.target.value }))} required />
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPw(!showPw)}><i className={`bi ${showPw ? 'bi-eye-slash' : 'bi-eye'}`} /></button>
                    </div>
                  </div>
                  <div className="col-md-4"><label className="form-label">New Password</label><input type={showPw ? 'text' : 'password'} className="form-control" value={pwForm.newPassword} onChange={(e) => setPwForm((p) => ({ ...p, newPassword: e.target.value }))} required minLength={6} /></div>
                  <div className="col-md-4">
                    <label className="form-label">Confirm New Password</label>
                    <input type={showPw ? 'text' : 'password'} className={`form-control ${pwForm.confirmPassword && pwForm.confirmPassword !== pwForm.newPassword ? 'is-invalid' : ''}`} value={pwForm.confirmPassword} onChange={(e) => setPwForm((p) => ({ ...p, confirmPassword: e.target.value }))} required />
                    {pwForm.confirmPassword && pwForm.confirmPassword !== pwForm.newPassword && <div className="invalid-feedback">Passwords do not match.</div>}
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button type="submit" className="btn btn-warning" disabled={pwSaving}>{pwSaving ? <><span className="spinner-border spinner-border-sm me-1" />Updating...</> : <><i className="bi bi-lock me-1" />Update Password</>}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function StudentAwareProfilePage() {
  const { user, tenant } = useAuth();
  const isStudent = String(user?.roleKey || '').toLowerCase() === 'student';
  return isStudent ? <StudentProfileLayout user={user} tenant={tenant} /> : <StaffProfileLayout />;
}
